import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { sanitizeFilename } from '@/utils/Export'

const SHARE_DIR = 'exports'
const CAPTURE_SCALE = 2
const SHARE_WIDTH = 1200
const MAX_CANVAS_PIXELS = 26e6
const CHART_RESIZE_TIMEOUT = 400
const TRIM_PADDING = 16
const HEADER_PADDING = 28
const HEADER_GAP = 10
const LOGO_SIZE = 26

let logoPromise = null

function cssVar(name, fallback) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name)
    return (value || '').trim() || fallback
}

export function themeTokens(isDark) {
    return {
        bg: cssVar('--v-bg-base', isDark ? '#080A0E' : '#F4F6F9'),
        surface: cssVar('--v-surface-base', isDark ? '#111520' : '#FFFFFF'),
        outline: cssVar('--v-outline-base', isDark ? '#2A3450' : '#E2E8F0'),
        txtPrimary: cssVar('--v-txtPrimary-base', isDark ? '#F2F5FA' : '#0F1623'),
        txtMuted: cssVar('--v-txtMuted-base', isDark ? '#556070' : '#A0AEC0'),
        accent: cssVar('--v-accent-base', '#339999'),
    }
}

function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => resolve(null)
        img.src = src
    })
}

function brandMark() {
    if (!logoPromise) logoPromise = loadImage(require('@/assets/img/brand_mark.png'))
    return logoPromise
}

const nextFrame = () => new Promise(resolve => requestAnimationFrame(() => resolve()))
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

async function waitForStableHeight(node) {
    await nextFrame()
    const start = Date.now()
    let previous = -1
    let stable = 0
    while (Date.now() - start < CHART_RESIZE_TIMEOUT) {
        const height = node.scrollHeight
        stable = height === previous ? stable + 1 : 0
        previous = height
        if (stable >= 2) break
        await delay(50)
    }
    await nextFrame()
}

let spinnerStyle = null

function ensureSpinnerStyle() {
    if (spinnerStyle) return
    spinnerStyle = document.createElement('style')
    spinnerStyle.textContent = '@keyframes share-capture-spin{to{transform:rotate(360deg)}}'
    document.head.appendChild(spinnerStyle)
}

function showCaptureOverlay(node, tokens, text) {
    ensureSpinnerStyle()
    const rect = node.getBoundingClientRect()
    const top = Math.max(0, rect.top)
    const height = Math.max(0, Math.min(window.innerHeight, rect.bottom) - top)

    const overlay = document.createElement('div')
    overlay.style.cssText = `position:fixed;left:${rect.left}px;top:${top}px;width:${rect.width}px;`
        + `height:${height}px;z-index:200;display:flex;flex-direction:column;align-items:center;`
        + `justify-content:center;gap:12px;background:${tokens.bg};color:${tokens.txtMuted};`
        + 'font-family:Outfit,sans-serif;font-size:13px;text-align:center;padding:0 24px'

    const spinner = document.createElement('div')
    spinner.style.cssText = 'width:28px;height:28px;border-radius:50%;border:3px solid '
        + `${tokens.outline};border-top-color:${tokens.accent};animation:share-capture-spin .8s linear infinite`
    overlay.appendChild(spinner)

    if (text) {
        const label = document.createElement('div')
        label.textContent = text
        overlay.appendChild(label)
    }

    document.body.appendChild(overlay)
    return () => overlay.remove()
}

async function chartInstances() {
    const Chart = (await import('@/plugins/chart')).default
    return Object.keys(Chart.instances || {}).map(key => Chart.instances[key])
}

function freezeAnimations(charts) {
    const durations = charts.map(chart => chart.options?.animation?.duration)
    charts.forEach(chart => {
        if (chart.options?.animation) chart.options.animation.duration = 0
    })
    return () => charts.forEach((chart, index) => {
        if (chart.options?.animation) chart.options.animation.duration = durations[index]
    })
}

async function withShareWidth(node, run, { tokens, loadingText }) {
    if (node.clientWidth >= SHARE_WIDTH) return run(node.clientWidth)

    const hideOverlay = showCaptureOverlay(node, tokens, loadingText)
    const parent = node.parentElement
    const previous = {
        width: node.style.width,
        maxWidth: node.style.maxWidth,
        visibility: node.style.visibility,
    }
    const previousParentOverflow = parent ? parent.style.overflow : null
    const charts = await chartInstances()
    const restoreAnimations = freezeAnimations(charts)

    node.style.width = `${SHARE_WIDTH}px`
    node.style.maxWidth = 'none'
    node.style.visibility = 'hidden'
    if (parent) parent.style.overflow = 'hidden'
    window.dispatchEvent(new Event('resize'))

    try {
        await nextFrame()
        charts.forEach(chart => chart.resize())
        await waitForStableHeight(node)
        return await run(SHARE_WIDTH)
    } finally {
        node.style.width = previous.width
        node.style.maxWidth = previous.maxWidth
        node.style.visibility = previous.visibility
        if (parent) parent.style.overflow = previousParentOverflow
        window.dispatchEvent(new Event('resize'))
        charts.forEach(chart => chart.resize())
        restoreAnimations()
        hideOverlay()
    }
}

function trimBottomBackground(canvas, scale) {
    const { width, height } = canvas
    if (!width || !height) return canvas

    const ctx = canvas.getContext('2d')
    const stripWidth = Math.min(8, width)
    const strips = [0, Math.floor((width - stripWidth) / 2), width - stripWidth]
        .map(x => ctx.getImageData(x, 0, stripWidth, height).data)

    const lastRow = (height - 1) * stripWidth * 4
    const reference = [strips[0][lastRow], strips[0][lastRow + 1], strips[0][lastRow + 2]]

    const isBackgroundRow = (row) => strips.every(data => {
        for (let i = 0; i < stripWidth; i++) {
            const pixel = (row * stripWidth + i) * 4
            if (data[pixel] !== reference[0]) return false
            if (data[pixel + 1] !== reference[1]) return false
            if (data[pixel + 2] !== reference[2]) return false
        }
        return true
    })

    let content = height - 1
    while (content > 0 && isBackgroundRow(content)) content--
    if (content === height - 1) return canvas

    const trimmedHeight = Math.min(height, content + 1 + Math.round(TRIM_PADDING * scale))
    if (trimmedHeight >= height) return canvas

    const trimmed = document.createElement('canvas')
    trimmed.width = width
    trimmed.height = trimmedHeight
    trimmed.getContext('2d').drawImage(canvas, 0, 0)
    return trimmed
}

function captureScale(width, height) {
    const area = Math.max(1, width * height)
    return Math.max(1, Math.min(CAPTURE_SCALE, Math.sqrt(MAX_CANVAS_PIXELS / area)))
}

export async function captureNode(node, background, width = 0) {
    if (!node) return null
    const html2canvas = (await import('html2canvas')).default
    const height = Math.max(node.scrollHeight, node.clientHeight)
    const captureWidth = width || node.clientWidth
    return html2canvas(node, {
        backgroundColor: background,
        scale: captureScale(captureWidth, height),
        useCORS: true,
        logging: false,
        width: captureWidth,
        height,
        windowWidth: captureWidth,
        windowHeight: height,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
            const clone = clonedDoc.querySelector(`[data-share-target="${node.dataset.shareTarget}"]`)
            if (!clone) return
            clone.style.overflow = 'visible'
            clone.style.height = 'auto'
            clone.style.maxHeight = 'none'
            clone.style.width = `${captureWidth}px`
            clone.style.maxWidth = 'none'
            clone.style.visibility = 'visible'
        },
    })
}

export async function composeReport(sectionCanvas, { title, period, subject, isDark, withLogo = true, scale = CAPTURE_SCALE }) {
    const tokens = themeTokens(isDark)
    const lines = [
        { text: title, size: 30, weight: 800, color: tokens.txtPrimary },
        { text: period, size: 15, weight: 500, color: tokens.txtMuted },
        { text: subject, size: 17, weight: 600, color: tokens.accent },
    ].filter(line => !!line.text)

    const headerHeight = (HEADER_PADDING * 2 + lines.reduce((acc, line) => acc + line.size + HEADER_GAP, -HEADER_GAP)) * scale
    const canvas = document.createElement('canvas')
    canvas.width = sectionCanvas.width
    canvas.height = sectionCanvas.height + headerHeight
    const ctx = canvas.getContext('2d')

    ctx.fillStyle = tokens.surface
    ctx.fillRect(0, 0, canvas.width, headerHeight)
    ctx.fillStyle = tokens.outline
    ctx.fillRect(0, headerHeight - scale, canvas.width, scale)

    ctx.textBaseline = 'top'
    let y = HEADER_PADDING * scale
    for (const line of lines) {
        ctx.font = `${line.weight} ${line.size * scale}px Outfit, sans-serif`
        ctx.fillStyle = line.color
        ctx.fillText(line.text, HEADER_PADDING * scale, y)
        y += (line.size + HEADER_GAP) * scale
    }

    if (withLogo) {
        const logo = await brandMark()
        const right = canvas.width - HEADER_PADDING * scale
        const top = HEADER_PADDING * scale
        ctx.font = `700 ${16 * scale}px Outfit, sans-serif`
        ctx.fillStyle = tokens.txtPrimary
        const wordmark = 'BuilderPro'
        const textWidth = ctx.measureText(wordmark).width
        ctx.fillText(wordmark, right - textWidth, top + (LOGO_SIZE - 16) / 2 * scale)
        if (logo) {
            const logoHeight = LOGO_SIZE * scale
            const ratio = logo.naturalWidth && logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : 1
            const logoWidth = logoHeight * ratio
            ctx.drawImage(logo, right - textWidth - 8 * scale - logoWidth, top, logoWidth, logoHeight)
        }
    }

    ctx.drawImage(sectionCanvas, 0, headerHeight)
    return canvas
}

function toBlob(canvas) {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(String(reader.result).split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

export function buildShareFileName(parts) {
    const name = parts.filter(Boolean).map(part => String(part).replace(/\//g, '-')).join(' - ')
    return `${sanitizeFilename(name)}.png`
}

export async function shareCanvas(canvas, filename, dialogTitle = '') {
    const blob = await toBlob(canvas)
    if (!blob) return

    if (Capacitor.getPlatform() === 'web') {
        const file = new File([blob], filename, { type: 'image/png' })
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: dialogTitle || filename })
            return
        }
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.click()
        URL.revokeObjectURL(url)
        return
    }

    const dirResult = await Filesystem.readdir({ path: SHARE_DIR, directory: Directory.Cache }).catch(() => null)
    if (!dirResult) {
        await Filesystem.mkdir({ path: SHARE_DIR, directory: Directory.Cache, recursive: true })
    }

    const savedFile = await Filesystem.writeFile({
        path: `${SHARE_DIR}/${filename}`,
        data: await blobToBase64(blob),
        directory: Directory.Cache,
    })

    await Share.share({
        title: dialogTitle || filename,
        files: [savedFile.uri],
        dialogTitle: dialogTitle || filename,
    })
}

export async function shareNodeAsImage(node, { title, period, subject, isDark, withLogo, filenameParts, dialogTitle, loadingText }) {
    const tokens = themeTokens(isDark)
    const captured = await withShareWidth(node, async (width) => ({
        canvas: await captureNode(node, tokens.bg, width),
        width,
    }), { tokens, loadingText })
    const rawCanvas = captured && captured.canvas
    if (!rawCanvas) return
    const scale = rawCanvas.width / captured.width
    const sectionCanvas = trimBottomBackground(rawCanvas, scale)
    const canvas = await composeReport(sectionCanvas, { title, period, subject, isDark, withLogo, scale })
    await shareCanvas(canvas, buildShareFileName(filenameParts || [title, period, subject]), dialogTitle || title)
}
